import { ViewChild, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICreditCreationReport,
} from '@module/credit-creation-reports/credit-creation-reports.types';
import {
  CreditCreationReportsService,
} from '@module/credit-creation-reports/services/credit-creation-reports.service';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { IApp } from '@core/models/app.interfaces';
import IDocument = IApp.IDocument;
import { environment } from '@env/environment';
import { UploadDocumentService } from '@shared/services/upload-document.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ServiceMessageComponent } from '@core/components/service-message/service-message.component';
import { DEFAULT_DIALOG_CONFIG, SereviceMessageType } from '@core/constants/serviceMessage.const';
import { ModalService } from '@shared/services/modal.service';
import { Dialog, DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { DocumentsComponent } from '@shared/components/documents/documents.component';
import { WCAGLabelDirective } from '@shared/directives/wcag-label.directive';
import { DisableOnUploadDirective } from '@shared/directives/disable-on-upload.directive';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@shared/components/ng-select/ng-select.module';
@Component({
  selector: 'app-add-details-form',
  templateUrl: './add-details-form.component.html',
  styleUrls: ['./add-details-form.component.scss'],
  imports: [TranslateModule, FormlyModule, DialogModule, ReactiveFormsModule, CommonModule,
    DocumentsComponent, ReactiveFormsModule, FormsModule, WCAGLabelDirective,
    DisableOnUploadDirective, FontAwesomeModule, NgSelectModule ]
})
export class AddDetailsFormComponent implements OnInit, OnDestroy {

  @ViewChild('dialog', { static: false }) dialog: Dialog;

  @Input() form = new FormGroup({});
  @Input() masterProject: any;
  @Output() modelIdUpdate = new EventEmitter();

  public report: ICreditCreationReport;
  public model: any = {};
  public fields: FormlyFieldConfig[];
  public needMoreInfoFields: FormlyFieldConfig[];
  public documents: IDocument[] = [];
  public isDocumentUploadInProgress = false;

  public visible = true;
  private _destroy$ = new Subject<any>();

  public currentStepObj;
  public downloadSelected;
  public taskList = [];
  public documentUploadDisabled = false;
  missingDocsCode: string[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<AddDetailsFormComponent>,
    private service: CreditCreationReportsService,
    private uploadDocumentService: UploadDocumentService,
    private translateService: TranslateService,
    private modalService: ModalService,
  ) { }

  ngOnInit() {
    this.currentStepObj = this.data.currentStepObj;
    if(this.currentStepObj.readOnly) {
      setTimeout(() => {
        this.currentStepObj.model = {...this.currentStepObj.model};
        this.currentStepObj.form.disable();
      });
    }
    this.data.section && (this.currentStepObj.section = this.data.section);
    this.currentStepObj.dialogRef = this.dialogRef;
    this.setTaskList();
    this.currentStepObj.onBeforeAddDetails && this.currentStepObj.onBeforeAddDetails();
  }

  scrollToTop(): void {
    setTimeout(() => {
      const content: HTMLElement = this.dialog?.el?.nativeElement?.querySelector('.p-dialog-content');
      if (content) {
        content.scrollTo(0, 0);
        if (!content.getAttribute('tabindex')) {
          content.setAttribute('tabindex', '-1');
        }
        content.focus();
      }
      const active = document.activeElement as HTMLElement;
      if (active) {
        active.blur();
        // Double-pass blur to handle rare timing cases
        requestAnimationFrame(() => {
          if (document.activeElement && document.activeElement !== content) {
            (document.activeElement as HTMLElement).blur();
          }
        });
      }
    });
  }

  public save() {
    if (!this.requiredNestedFields) {
      return;
    }
    if (this.currentStepObj.detailSectionDocumentUploadConfig &&
        !this.uploadDocumentService.checkMandatoryDocuments(
          this.missingDocsCode,
          this.currentStepObj.detailSectionDocumentUploadConfig,
          this.currentStepObj.optionalDocsWithPlaceholderCodes,
        )) {
        return;
    }
    this.currentStepObj.save(this.dialogRef);
  }

  public close(): void {
    if (this.currentStepObj.form.dirty && this.currentStepObj.form.touched && !this.currentStepObj.readOnly) {
      const sub$ = this.service.openConfirmationModal('confirmOnClose').subscribe(result => {
        sub$.unsubscribe();
        if (result) {
          this._close();
        }
      });
    } else {
      this._close();
    }
  }

  documentUploadHandler(documentResult: IApp.IEmmitedDocs): void {
    this.documents = documentResult.docs;
    this.missingDocsCode = documentResult.missingDocsCode;
    this.isDocumentUploadInProgress = documentResult.docs
      .filter(document => document._uploadStatus)
      .some(document => document._uploadStatus !== 'CLEAN');
  }

  public downloadTemplate(ev) {
    let uri = '/account-service/document/downloadTemplate/';
    const temlateMap = {
      DOCUMENT_REQUIRED_FOR_INFROMATION_ON_PUBLIC_CHARGING_STATIONS: {
        en: 'PublicChargingStation.xlsx',
        fr: 'BorneDeRechargePublique.xlsx'
      },
      DOCUMENT_REQUIRED_FOR_INFROMATION_ON_PRIVATE_CHARGING_STATIONS: {
        en: 'PrivateChargingStation.xlsx',
        fr: 'BorneDeRechargePrivee.xlsx'
      }
    }

    if (ev.id) {
      uri += temlateMap[ev.id][this.translateService.currentLang];
      this.uploadDocumentService.downloadDocument(`${ environment.apiUrl }${uri}`);
      setTimeout(() => {
        this.downloadSelected = null;
        (document.activeElement as HTMLElement).blur();
        }, 0,
      );
    }
  }

  private setTaskList() {
    if (this._hasDocumentConfigs() && this.service.currentStep.code === 'STEP-4'){
          this.taskList = this.currentStepObj.detailSectionDocumentUploadConfig.documentUploadConfigs.map(entity => {
            return {
              id: entity.documentTypeCode,
              name: this.translateService.instant(`COMMON.documentList.${ entity.documentTypeCode}`)
            }
          });
        }
  }

  private get requiredNestedFields(): boolean {
    const missingNestedFields = this.currentStepObj.validateRequiredNestedFields;
    if (Object.keys(missingNestedFields).length) {
      this.nestedFieldsRequired(missingNestedFields);
      return false;
    } else {
      this.currentStepObj.form.updateValueAndValidity();
      return true;
    }
  }

  private nestedFieldsRequired(missingFields) {
    let msg = '';
    Object.keys(missingFields).forEach(key => msg += `<li>${ this.translateService.instant(missingFields[key]) }</li>`);
    const metaData = [`<ol type="a">${ msg }</ol>`];
    this.modalService.open(
      ServiceMessageComponent,
      {
        messages: [
          {
            message: 'mandatoryNestedFieldsRequired',
            metaData,
          },
        ],
        type: SereviceMessageType.ERROR,
      },
      true,
      DEFAULT_DIALOG_CONFIG,
    );
  }

  ngOnDestroy() {
    this._destroy$.next(null);
    this._destroy$.complete();
    this.currentStepObj.fuelEnergyDensity$.next([]);
  }

  private _hasDocumentConfigs() {
    return this.currentStepObj.detailSectionDocumentUploadConfig &&
      this.currentStepObj.detailSectionDocumentUploadConfig.documentUploadConfigs;
  }

  private _close() {
    if (this.currentStepObj.model.edit
        && this.currentStepObj.detailSectionDocumentUploadConfig
        && !this.uploadDocumentService.checkMandatoryDocuments(
          this.missingDocsCode,
          this.currentStepObj.detailSectionDocumentUploadConfig,
          this.currentStepObj.optionalDocsWithPlaceholderCodes,
        )
    ) {
      return
    } else {
      this.dialogRef.close(false);
    }
  }

}
